<template>
  <b-card
    class="pt-1"
    no-body
  >
    <b-card-header class="pt-0 pb-1">
      <b-card-title class="mb-0">
        {{ $tk('ClientPortal.Property.Earnings.Placements') }}
      </b-card-title>
      <div class="text-right">
        <p
          v-if="compare"
          class="font-weight-bold mb-0"
        >
          {{ $tk('ClientPortal.Property.Earnings.FirstPeriod') }}
          <i class="dot" />
        </p>
        <p
          v-if="compare"
          class="font-weight-bold mb-0"
        >
          {{ $tk('ClientPortal.Property.Earnings.SecondPeriod') }}
          <i class="dot bg-warning" />
        </p>
      </div>

    </b-card-header>
    <b-skeleton-table
      v-if="loadingPlacementStats"
      :rows="5"
      :columns="11"
    />
    <b-table-simple
      v-if="!loadingPlacementStats"
      class="table table-responsive client-table__data"
      responsive
    >
      <b-thead>
        <b-tr>
          <b-th>
            {{ $tk('ClientPortal.Property.Earnings.Placement') }}
          </b-th>
          <b-th class="text-right">
            {{ $tk('ClientPortal.Property.Earnings.Views') }}
          </b-th>
          <b-th class="text-right">
            {{ $tk('ClientPortal.Property.Earnings.UniqueViews') }}
          </b-th>
          <b-th class="text-right">
            {{ $tk('ClientPortal.Property.Earnings.Clicks') }}
          </b-th>
          <b-th class="text-right">
            {{ $tk('ClientPortal.Property.Earnings.UniqueClicks') }}
          </b-th>
          <b-th class="text-right">
            {{ $tk('ClientPortal.Property.Earnings.Actions') }}
          </b-th>
          <b-th class="text-right">
            {{ $tk('ClientPortal.Property.Earnings.UniqueActions') }}
          </b-th>
          <b-th class="text-right">
            {{ $tk('ClientPortal.Property.Earnings.CTR') }}
          </b-th>
          <b-th class="text-right">
            {{ $tk('ClientPortal.Property.Earnings.FillRate') }}
          </b-th>
          <b-th class="text-right">
            {{ $tk('ClientPortal.Property.Earnings.TotalRequests') }}
          </b-th>
          <b-th class="text-right">
            {{ $tk('ClientPortal.Property.Earnings.Earnings') }}
          </b-th>
        </b-tr>
      </b-thead>
      <template v-if="placementStats.length === 0">
        <b-tfoot>
          <b-tr>
            <b-td
              colspan="10"
              class="text-center p-2"
            >
              <GjIcon
                size="64px"
                name="Widget"
              />
              <div class="mb-50">
                {{ $tk('ClientPortal.Property.Earnings.NoPlacementsHaveBeenCreatedYet') }}
              </div>
            </b-td>
          </b-tr>
        </b-tfoot>
      </template>
      <b-tbody v-else>
        <b-tr
          v-for="placementStat in placementStats"
          :key="placementStat.id"
        >
          <b-td>{{ placementStat.name }}</b-td>
          <b-td class="text-right">
            <span :class="{ 'chart-first-filter': compare }">
              {{ placementStat.firstPeriod.views | formatInteger }}
            </span>
            <hr
              v-if="compare"
              class="my-25"
            >
            <span
              v-if="compare"
              class="chart-second-filter"
            >
              {{ placementStat.secondPeriod.views | formatInteger }}
            </span>
          </b-td>
          <b-td class="text-right">
            <span :class="{ 'chart-first-filter': compare }">
              {{ placementStat.firstPeriod.uniqueViews | formatInteger }}
            </span>
            <hr
              v-if="compare"
              class="my-25"
            >
            <span
              v-if="compare"
              class="chart-second-filter"
            >
              {{ placementStat.secondPeriod.uniqueViews | formatInteger }}
            </span>
          </b-td>
          <b-td class="text-right">
            <span :class="{ 'chart-first-filter': compare }">
              {{ placementStat.firstPeriod.clicks | formatInteger }}
            </span>
            <hr
              v-if="compare"
              class="my-25"
            >
            <span
              v-if="compare"
              class="chart-second-filter"
            >
              {{ placementStat.secondPeriod.clicks | formatInteger }}
            </span>
          </b-td>
          <b-td class="text-right">
            <span :class="{ 'chart-first-filter': compare }">
              {{ placementStat.firstPeriod.uniqueClicks | formatInteger }}
            </span>
            <hr
              v-if="compare"
              class="my-25"
            >
            <span
              v-if="compare"
              class="chart-second-filter"
            >
              {{ placementStat.secondPeriod.uniqueClicks | formatInteger }}
            </span>
          </b-td>
          <b-td class="text-right">
            <span :class="{ 'chart-first-filter': compare }">
              {{ placementStat.firstPeriod.actions | formatInteger }}
            </span>
            <hr
              v-if="compare"
              class="my-25"
            >
            <span
              v-if="compare"
              class="chart-second-filter"
            >
              {{ placementStat.secondPeriod.actions | formatInteger }}
            </span>
          </b-td>
          <b-td class="text-right">
            <span :class="{ 'chart-first-filter': compare }">
              {{ placementStat.firstPeriod.uniqueActions | formatInteger }}
            </span>
            <hr
              v-if="compare"
              class="my-25"
            >
            <span
              v-if="compare"
              class="chart-second-filter"
            >
              {{ placementStat.secondPeriod.uniqueActions | formatInteger }}
            </span>
          </b-td>
          <b-td class="text-right">
            <span :class="{ 'chart-first-filter': compare }">
              {{ placementStat.firstPeriod.ctr | formatNumber }}%
            </span>
            <hr
              v-if="compare"
              class="my-25"
            >
            <span
              v-if="compare"
              class="chart-second-filter"
            >
              {{ placementStat.secondPeriod.ctr | formatNumber }}%
            </span>
          </b-td>
          <b-td class="text-right">
            <span :class="{ 'chart-first-filter': compare }">
              {{ placementStat.firstPeriod.fillRate | formatNumber }}
            </span>
            <hr
              v-if="compare"
              class="my-25"
            >
            <span
              v-if="compare"
              class="chart-second-filter"
            >
              {{ placementStat.secondPeriod.fillRate | formatNumber }}
            </span>
          </b-td>
          <b-td class="text-right">
            <span :class="{ 'chart-first-filter': compare }">
              {{ placementStat.firstPeriod.totalRequests | formatInteger }}
            </span>
            <hr
              v-if="compare"
              class="my-25"
            >
            <span
              v-if="compare"
              class="chart-second-filter"
            >
              {{ placementStat.secondPeriod.totalRequests | formatInteger }}
            </span>
          </b-td>
          <b-td class="text-right">
            <span :class="{ 'chart-first-filter': compare }">
              {{ placementStat.firstPeriod.profit | formatCurrency }}
            </span>
            <hr
              v-if="compare"
              class="my-25"
            >
            <span
              v-if="compare"
              class="chart-second-filter"
            >
              {{ placementStat.secondPeriod.profit | formatCurrency }}
            </span>
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
  </b-card>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'

export default {
  components: {
    GjIcon,
  },
  props: {
    placementStats: {
      type: Array,
      default: () => [],
    },
    compare: {
      type: Boolean,
      default: false,
    },
    loadingPlacementStats: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<!-- Create new scss file if you add more than two classes inside component -->
<style lang="scss" scoped>
.chart-first-filter {
color: #1e88e5;
}

.chart-second-filter {
    color: #d4754d;
}
</style>
